import { Checkbox, Form, Input, InputNumber, message, Modal, Select } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import { t } from 'ttag';

import Label from 'common/components/Label';
import UnstyledCollapse from 'common/components/UnstyledCollapse';

import { usePackagesQuery } from '../../Packages/queries';

import { useCreateOrderMutation } from './queries';

const MAX_EVENT_CREDITS = 50;

const ERROR_MESSAGES_BY_CODE = () => ({
  'bc709319-e3e3-487f-8484-756d36c0f87a': t`Order must contain at least one credit.`,
  '9a907e06-6baf-4318-b16a-246f7706ed7c': t`Order contains more than maximum allowed event credits.`,
  '314595e1-215d-4274-bdc2-c6236372754a': t`Organization already has an active people credit`,
});

const NewOrderModal = ({ onClose, organization }) => {
  const [form] = Form.useForm();
  const currentIsForInternalUse = Form.useWatch('isForInternalUse', form);

  const { data: packages = [], isLoading: packagesLoading } = usePackagesQuery({
    type: 'event_credit',
  });
  const createOrderMutation = useCreateOrderMutation();

  const handleSubmit = async values => {
    const payload = {
      ...values,
      credits: values.credits.map(credit => ({
        ...credit,
        features: credit.features || {}, // No features as per requirements
        additionalData: credit.additionalData || {},
      })),
    };

    createOrderMutation.mutate(payload, {
      onSuccess: () => {
        onClose(true);
        message.success(t`Order created successfully`);
      },
      onError: err => {
        const errorMessage =
          ERROR_MESSAGES_BY_CODE()[err?.errors?.[0]?.code] ??
          t`Unknown error occurred while creating order.`;

        message.error(errorMessage);
      },
    });
  };

  return (
    <Modal
      title={t`New Order`}
      open
      width={600}
      destroyOnClose
      okText={t`Complete Order`}
      okButtonProps={{
        loading: createOrderMutation.isLoading,
        htmlType: 'submit',
        form: 'new-order-form',
      }}
      cancelText={t`Cancel`}
      cancelButtonProps={{ disabled: createOrderMutation.isLoading }}
      onCancel={() => onClose(false)}
    >
      <Form
        form={form}
        id="new-order-form"
        layout="vertical"
        requiredMark={false}
        onFinish={handleSubmit}
        initialValues={{ organizationId: organization.id, isForInternalUse: true }}
      >
        <Form.Item name="organizationId" hidden />
        <Form.Item label={<Label>{t`Organization`}</Label>}>
          <Input value={organization.name} disabled />
        </Form.Item>

        <Form.Item name="isForInternalUse" valuePropName="checked">
          <Checkbox>
            {t`These credits are for internal use and will not be given to a client`}
          </Checkbox>
        </Form.Item>

        <UnstyledCollapse condition={!currentIsForInternalUse}>
          <Form.Item
            // Yes yes - Salesforce != HubSpot - I know
            // The HubSpot ID is stored in the `salesforceOpportunityId` field
            // in the backend. This API is old and not worth the effort to rename in Flux.
            name="salesforceOpportunityId"
            label={<Label>{t`HubSpot Deal ID`}</Label>}
            rules={[{ required: true, message: t`HubSpot Deal ID is required.` }]}
          >
            <Input />
          </Form.Item>
        </UnstyledCollapse>

        <Form.Item name={['credits', 0, 'creditType']} hidden initialValue="event_credit" />

        <Form.Item
          name={['credits', 0, 'blueprintId']}
          label={<Label>{t`Package`}</Label>}
          rules={[{ required: true, message: t`Package is required.` }]}
        >
          <Select
            placeholder={t`Select a package`}
            loading={packagesLoading}
            showSearch
            filterOption={(input, { label }) =>
              label.toLocaleLowerCase().includes(input.toLocaleLowerCase())
            }
            options={packages.map(pkg => ({ label: pkg.name, value: pkg.id }))}
          />
        </Form.Item>

        <Form.Item
          name={['credits', 0, 'name']}
          label={<Label>{t`Credit Name`}</Label>}
          rules={[
            { required: true, message: t`Credit Name is required.` },
            { max: 100, message: t`Credit Name should be in the range of 1 to 100 characters.` },
          ]}
          extra={t`The credit name is displayed when creating an event.`}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name={['credits', 0, 'quantity']}
          label={<Label>{t`Number of Events`}</Label>}
          rules={[
            { required: true, message: t`Number of Events is required.` },
            {
              type: 'number',
              min: 1,
              max: MAX_EVENT_CREDITS,
              message: t`Number of Events must be between 1 and ${MAX_EVENT_CREDITS}.`,
            },
          ]}
        >
          <InputNumber min={1} max={MAX_EVENT_CREDITS} style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

NewOrderModal.propTypes = {
  organization: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default NewOrderModal;
